import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import Header from "../../components/ui/Header";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import Footer from "../adaptive-homepage-hub/components/Footer";

const AboutPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Helmet>
        <title>About - SkycrestFilms | Cinematic Video Production</title>
        <meta
          name="description"
          content="SkycrestFilms is a full‑service video production studio crafting cinematic stories for brands, real estate, events, and design fiction."
        />
      </Helmet>

      <Header />

      {/* Hero */}
      <section className="pt-24 pb-12 bg-gradient-to-b from-white/60 to-white/20">
        <div className="container mx-auto px-6 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">
            Crafting Film That Moves People
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            We are a cinematic video production studio blending strategy, story,
            and craft to create work that performs as beautifully as it looks.
          </p>
          <div className="mt-8 flex justify-center gap-4">
            <Button
              variant="default"
              className="bg-cta text-cta-foreground"
              iconName="Play"
              iconPosition="left"
            >
              Watch Our Reel
            </Button>
            <Button
              variant="outline"
              iconName="Calendar"
              iconPosition="left"
              asChild
            >
              <a
                href="#book-consultation"
                onClick={(e) => {
                  e.preventDefault();
                  if (!document.getElementById("MeetingsEmbedCode-js")) {
                    const s = document.createElement("script");
                    s.src =
                      "https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js";
                    s.async = true;
                    s.defer = true;
                    s.id = "MeetingsEmbedCode-js";
                    document.body.appendChild(s);
                  }
                  let overlay = document.querySelector("#hs-meetings-overlay");
                  if (!overlay) {
                    overlay = document.createElement("div");
                    overlay.id = "hs-meetings-overlay";
                    overlay.className =
                      "fixed inset-0 z-[60] flex justify-center items-start p-0 bg-[#425B76] md:bg-black/50";
                    overlay.style.overflow = "hidden";
                    overlay.style.overscrollBehavior = "contain";

                    const inner = document.createElement("div");
                    inner.className =
                      "relative w-screen h-screen md:w-full md:max-w-4xl md:h-[90vh] bg-[#425B76] md:bg-transparent rounded-none shadow-none p-0";
                    inner.style.overflow = "hidden";

                    const iframe = document.createElement("iframe");
                    iframe.src =
                      "https://meetings-na2.hubspot.com/brian-wilkinson?embed=true";
                    iframe.style.width = "100%";
                    iframe.style.height = "100%";
                    iframe.style.minWidth = "312px";
                    iframe.style.minHeight = "0";
                    iframe.style.border = "none";
                    iframe.style.overflow = "hidden";
                    iframe.style.overscrollBehavior = "contain";
                    iframe.style.display = "block";
                    iframe.setAttribute("scrolling", "no");
                    iframe.setAttribute("data-hs-ignore", "true");

                    // Mobile-only stretch to remove inner gutters without blurring
                    const isMobile =
                      window.matchMedia("(max-width: 767px)").matches;
                    if (isMobile) {
                      iframe.style.position = "absolute";
                      iframe.style.width = "110vw";
                      iframe.style.height = "110vh";
                      iframe.style.left = "-5vw";
                      iframe.style.top = "-5vh";
                    } else {
                      iframe.style.position = "static";
                      iframe.style.width = "100%";
                      iframe.style.height = "100%";
                      iframe.style.left = "0";
                      iframe.style.top = "0";
                    }

                    const close = document.createElement("button");
                    close.className =
                      "fixed top-4 right-4 md:absolute md:top-[16px] md:right-[-3px] z-[70] text-gray-500 hover:text-gray-900 bg-white hover:bg-white/80 rounded";
                    close.innerText = "×";
                    close.style.fontSize = "18px";
                    close.style.lineHeight = "1";
                    close.style.margin = "0";
                    close.style.padding = "2px 9px";
                    close.style.paddingBottom = "6px";
                    close.onclick = () => {
                      document.body.style.overflow = "";
                      overlay.remove();
                    };
                    inner.appendChild(iframe);
                    inner.appendChild(close);
                    overlay.appendChild(inner);
                    document.body.appendChild(overlay);
                    document.body.style.overflow = "hidden";
                  }
                }}
              >
                Book Consultation
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* What we do */}
      <section className="py-16">
        <div className="container mx-auto px-6 grid md:grid-cols-3 gap-10">
          <div>
            <h2 className="text-2xl font-semibold mb-3">Strategy First</h2>
            <p className="text-muted-foreground">
              Every project starts with intent. We define audiences, messages,
              and success metrics before a single frame is captured.
            </p>
          </div>
          <div>
            <h2 className="text-2xl font-semibold mb-3">Cinematic Craft</h2>
            <p className="text-muted-foreground">
              From location to lens choice, lighting to sound, our productions
              are designed to feel premium and authentic.
            </p>
          </div>
          <div>
            <h2 className="text-2xl font-semibold mb-3">Results That Matter</h2>
            <p className="text-muted-foreground">
              Launches, leads, bookings or brand love — we build films that
              deliver measurable outcomes.
            </p>
          </div>
        </div>
      </section>

      {/* Services snapshot */}
      <section className="py-16 bg-surface">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-10">Capabilities</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: "Heart",
                title: "Events & Weddings",
                desc: "Elegant coverage and emotive storytelling for once‑in‑a‑lifetime moments.",
              },
              {
                icon: "Building",
                title: "Real Estate",
                desc: "Architectural films and property tours that showcase space and lifestyle.",
              },
              {
                icon: "Target",
                title: "Brand & Marketing",
                desc: "Launch films, brand stories, commercials, and social cut‑downs.",
              },
              {
                icon: "Sparkles",
                title: "Design Fiction",
                desc: "Concept visualization for future products, spaces, and ideas.",
              },
              {
                icon: "Edit",
                title: "Post‑Production",
                desc: "Editing, color, sound, graphics, and delivery for every platform.",
              },
              {
                icon: "Settings",
                title: "Production Services",
                desc: "Crews, casting, locations, permits, insurance, and logistics.",
              },
            ].map((s) => (
              <div
                key={s.title}
                className="p-6 rounded-2xl bg-card border border-border/50"
              >
                <div className="w-10 h-10 rounded-lg bg-accent/10 text-accent flex items-center justify-center mb-3">
                  <Icon name={s.icon} size={18} />
                </div>
                <h3 className="text-lg font-semibold mb-1">{s.title}</h3>
                <p className="text-sm text-muted-foreground">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Approach */}
      <section className="py-16">
        <div className="container mx-auto px-6 grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <h2 className="text-3xl font-bold mb-4">Our Approach</h2>
            <ul className="space-y-4 text-muted-foreground">
              <li className="flex gap-3">
                <Icon name="Search" size={18} className="text-accent mt-1" />
                Discovery workshops to align goals and audience
              </li>
              <li className="flex gap-3">
                <Icon
                  name="Storyboard"
                  size={18}
                  className="text-accent mt-1"
                />
                Treatment, script, and storyboard development
              </li>
              <li className="flex gap-3">
                <Icon name="Camera" size={18} className="text-accent mt-1" />
                Cinematic production with senior crew and premium gear
              </li>
              <li className="flex gap-3">
                <Icon name="Edit" size={18} className="text-accent mt-1" />
                Editorial, color, sound and graphics in‑house
              </li>
              <li className="flex gap-3">
                <Icon name="BarChart2" size={18} className="text-accent mt-1" />
                Delivery optimized for web, social, and broadcast
              </li>
            </ul>
          </div>
          <div className="rounded-2xl bg-card border border-border/50 p-6">
            <h3 className="text-xl font-semibold mb-2">
              Trusted by teams of all sizes
            </h3>
            <p className="text-muted-foreground mb-4">
              From boutique brands to enterprise marketing teams, we adapt to
              your workflow and bring the right crew for the job.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm text-muted-foreground">
              <div className="p-3 rounded-lg bg-muted">Agencies</div>
              <div className="p-3 rounded-lg bg-muted">Real Estate</div>
              <div className="p-3 rounded-lg bg-muted">Hospitality</div>
              <div className="p-3 rounded-lg bg-muted">Tech</div>
              <div className="p-3 rounded-lg bg-muted">Lifestyle</div>
              <div className="p-3 rounded-lg bg-muted">Events</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-white/60 to-white/20">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Let's create something exceptional
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            Tell us your goals and we’ll design a production plan that fits your
            budget, timeline, and audience.
          </p>
          <Button
            variant="default"
            className="bg-cta text-cta-foreground"
            iconName="Calendar"
            iconPosition="left"
          >
            Schedule a Call
          </Button>
        </div>
      </section>

      <Footer currentTheme="default" />
    </div>
  );
};

export default AboutPage;
